/* Favourites / wishlist (native, no app, no backend).
 * Storage: localStorage only, so it is instant and free but per-browser (does not sync across
 * devices or survive a cleared browser). Ids are Shopify product ids; handle is kept alongside
 * so the favourites page can fetch each product's live data without a lookup table.
 *
 * Public API: window.KGWishlist.{ getAll, has, add, remove, toggle, count }
 * Event: document dispatches "kg:wishlist-changed" with { detail: { ids, count } } on every change.
 */
(function () {
  if (window.KGWishlist) return;

  var STORAGE_KEY = 'kg_wishlist_v1';

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      // Private browsing / storage disabled: the toggle still works for the current page view,
      // it just will not persist. Fail silently rather than break add-to-cart-adjacent UI.
    }
    var ids = list.map(function (item) { return String(item.id); });
    document.dispatchEvent(new CustomEvent('kg:wishlist-changed', { detail: { ids: ids, count: ids.length } }));
    return ids;
  }

  function has(id) {
    id = String(id);
    return read().some(function (item) { return String(item.id) === id; });
  }

  function add(product) {
    var id = String(product.id);
    var list = read();
    if (list.some(function (item) { return String(item.id) === id; })) return read().map(function (i) { return String(i.id); });
    list.push({ id: id, handle: product.handle || '', addedAt: Date.now() });
    return write(list);
  }

  function remove(id) {
    id = String(id);
    var list = read().filter(function (item) { return String(item.id) !== id; });
    return write(list);
  }

  function toggle(product) {
    var id = String(product.id);
    return has(id) ? remove(id) : add(product);
  }

  window.KGWishlist = {
    getAll: read,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    count: function () { return read().length; }
  };

  /* ---- UI binding: any [data-kg-wishlist-toggle] button on the page, plus the header badge ---- */

  function syncButton(btn, activeIds) {
    var id = btn.dataset.productId;
    var active = activeIds.indexOf(id) !== -1;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    var label = active ? btn.dataset.labelRemove : btn.dataset.labelAdd;
    if (label) btn.setAttribute('aria-label', label);
    var textEl = btn.querySelector('.kg-wishlist-btn__label');
    if (textEl && label) textEl.textContent = label;
  }

  function syncAllButtons(activeIds) {
    document.querySelectorAll('[data-kg-wishlist-toggle]').forEach(function (btn) {
      syncButton(btn, activeIds);
    });
  }

  function syncHeaderBadge(count) {
    document.querySelectorAll('[data-kg-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.closest('[data-kg-wishlist-badge]') && (el.closest('[data-kg-wishlist-badge]').hidden = count === 0);
    });
  }

  // Small pop animation on the icon so add/remove is visually obvious. Direction-aware:
  // a "burst" pop when adding, a quick "deflate" when removing. See .kg-wishlist-btn--pop-*
  // in assets/main.css.
  function playToggleAnimation(btn, wasActive) {
    var cls = wasActive ? 'kg-wishlist-btn--pop-remove' : 'kg-wishlist-btn--pop-add';
    btn.classList.remove('kg-wishlist-btn--pop-add', 'kg-wishlist-btn--pop-remove');
    // Force a reflow so the animation restarts even on rapid repeated clicks.
    void btn.offsetWidth;
    btn.classList.add(cls);
    btn.addEventListener('animationend', function handler() {
      btn.classList.remove(cls);
      btn.removeEventListener('animationend', handler);
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-kg-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    var id = btn.dataset.productId;
    if (!id) return;
    var wasActive = window.KGWishlist.has(id);
    window.KGWishlist.toggle({ id: id, handle: btn.dataset.productHandle || '' });
    playToggleAnimation(btn, wasActive);
  });

  document.addEventListener('kg:wishlist-changed', function (e) {
    syncAllButtons(e.detail.ids);
    syncHeaderBadge(e.detail.count);
  });

  function initialSync() {
    var list = read();
    var ids = list.map(function (item) { return String(item.id); });
    syncAllButtons(ids);
    syncHeaderBadge(ids.length);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialSync);
  } else {
    initialSync();
  }

  /* ---- Favourites page grid: [data-kg-wishlist-page] fetches sections/kg-wishlist-cards.liquid
     via the Section Rendering API, so saved products render with the exact same product-card
     markup (price, badges, swatches, the heart button itself) as everywhere else on the site.
     The heart button on each card doubles as the remove control - toggling it off fires
     kg:wishlist-changed, which re-runs this render with the shorter list. ---- */

  function renderWishlistPage(root) {
    var list = read();
    var emptyEl = root.querySelector('[data-kg-wishlist-empty]');
    var gridEl = root.querySelector('[data-kg-wishlist-grid]');
    if (!gridEl) return;
    var handles = list.map(function (item) { return item.handle; }).filter(Boolean);
    if (!handles.length) {
      gridEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    gridEl.setAttribute('aria-busy', 'true');
    var searchUrl = root.dataset.searchUrl || '/search';
    var marker = 'kg-wishlist:' + handles.join(',');
    var url = searchUrl + '?q=' + encodeURIComponent(marker) + '&type=article&section_id=kg-wishlist-cards';
    fetch(url, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.text() : ''; })
      .then(function (html) {
        gridEl.removeAttribute('aria-busy');
        var temp = document.createElement('div');
        temp.innerHTML = html;
        var ul = temp.querySelector('[data-kg-wishlist-cards-grid]');
        if (ul && ul.children.length) {
          gridEl.innerHTML = ul.outerHTML;
          syncAllButtons(read().map(function (item) { return String(item.id); }));
          // Cards fetched after DOMContentLoaded need their product/lifestyle view applied and
          // their "Fler färger" swatch-hover preview bound - see assets/product-card-view-toggle.js.
          window.KGProductCardViewToggle && window.KGProductCardViewToggle.initCards(gridEl);
        } else {
          gridEl.innerHTML = '';
          if (emptyEl) emptyEl.hidden = false;
        }
      })
      .catch(function () {
        gridEl.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('kg:wishlist-changed', function () {
    var root = document.querySelector('[data-kg-wishlist-page]');
    if (root) renderWishlistPage(root);
  });

  function initWishlistPage() {
    var root = document.querySelector('[data-kg-wishlist-page]');
    if (root) renderWishlistPage(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlistPage);
  } else {
    initWishlistPage();
  }
})();
